import * as React from 'react';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import ReCAPTCHA from 'react-google-recaptcha';
import { enqueueSnackbar } from 'notistack';
import { firebaseApp } from 'firebase-app';
import { connectFunctionsEmulator, getFunctions, httpsCallable } from 'firebase/functions';
import { z } from 'zod';

const functions = getFunctions(firebaseApp, 'europe-west1');

if (location.hostname === 'localhost') {
  connectFunctionsEmulator(functions, '127.0.0.1', 5001);
}

const contactFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be 100 characters or less'),
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  message: z.string().min(1, 'Message is required').max(2000, 'Message must be 2000 characters or less'),
});

type ContactFormValues = z.infer<typeof contactFormSchema>;

interface ContactFormResponse {
  success: boolean;
  error?: string;
  fields?: {
    name?: string;
    email?: string;
    message?: string;
  };
}

interface ContactFormProps {
  initialMessage?: string;
}

export const ContactForm: React.FC<ContactFormProps> = ({ initialMessage }) => {
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: { name: '', email: '', message: initialMessage || '' },
  });

  const messageValue = watch('message');


  const onSubmit = async (data: ContactFormValues) => {
    if (!recaptchaToken) {
      setRecaptchaError(true);
      return;
    }
    setRecaptchaError(false);

    try {
      const submitContactForm = httpsCallable<
        ContactFormValues & { recaptchaToken: string },
        ContactFormResponse
      >(functions, 'submitContactForm');

      const response = await submitContactForm({ ...data, recaptchaToken });
      const result = response.data;

      if (result.success) {
        enqueueSnackbar('Thank you for your inquiry. We\'ll get back to you soon.', { variant: 'success' });
        reset();
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } else if (result.fields) {
        if (result.fields.name) setError('name', { message: result.fields.name });
        if (result.fields.email) setError('email', { message: result.fields.email });
        if (result.fields.message) setError('message', { message: result.fields.message });
      } else {
        enqueueSnackbar(result.error || 'Something went wrong. Please try again later.', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Something went wrong. Please try again later.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 600, width: '100%' }}>
      <Typography variant="h5" sx={{ marginBottom: 3 }}>
        Contact Us
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={3}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                required
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                inputProps={{ maxLength: 100 }}
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                type="email"
                required
                fullWidth
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
          <Controller
            name="message"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Message"
                required
                fullWidth
                multiline
                rows={5}
                error={!!errors.message}
                helperText={errors.message?.message || `${messageValue?.length || 0}/2000`}
                inputProps={{ maxLength: 2000 }}
              />
            )}
          />
          <Box>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
              onChange={(token) => {
                setRecaptchaToken(token);
                setRecaptchaError(false);
              }}
              onExpired={() => setRecaptchaToken(null)}
            />
            {recaptchaError && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                Please complete the CAPTCHA
              </Typography>
            )}
          </Box>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#5D7243',
              color: '#E9F073',
              borderRadius: '24px',
              padding: '12px 24px',
              fontSize: '16px',
              '&:hover': { backgroundColor: '#4a5c35' },
              '&:disabled': { backgroundColor: '#8a9a78', color: '#c5d4a0' },
            }}
          >
            {isSubmitting ? <CircularProgress size={24} sx={{ color: '#E9F073' }} /> : 'Send Message'}
          </Button>
        </Stack>
      </form>
    </Box>
  );
};
